import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DetailedPage, PageStyle } from '@models/Page';
import PageSection, {
  MinifiedPageSection,
  RegularSectionStyleType,
  RegularSectionStyleTypes,
  StaticSectionStyleType,
  StaticSectionStyleTypes,
} from '@models/PageSection';
import {
  getDefaultSectionStyleType,
  getDefaultStylePattern,
  getValidSectionStyleTypes,
  isSectionStylePatternValid,
} from '@modules/layouts/pages/PageStyleRules';
import { withNewSourceInstanceId } from '@modules/layouts/pages/utils';
import { getItemWithRealSlug } from '@utils/layoutEntityUtils';
import { removeImagesFromPageSection } from '@utils/pageUtils';

import { RootState } from '..';

import {
  AddSectionPayload,
  AddSectionSourcesPayload,
  ClearSectionPayload,
  DuplicateSectionPayload,
  RemoveSectionPayload,
  RemoveSectionSourcePayload,
  ReorderSectionSourcesPayload,
  ReorderSectionsPayload,
  SetNamePayload,
  SetOrderProvidersByIdPayload,
  SetOrderProvidersByTypePayload,
  SetPageMenuPayload,
  SetPagePayload,
  SetSlugPayload,
  SetStylePayload,
  UpdateSectionPayload,
  UpdateSectionSourcePayload,
  UpdateThemePayload,
} from './types';

export const NEW_PAGE_ID = 'NEW_PAGE_ID';

export const getEmptyPage = (): DetailedPage => ({
  createdBy: '',
  createdDateTime: '',
  id: '',
  name: { no: '', en: '', da: '' },
  sections: [],
  slug: '',
  style: 'swimlane',
  theme: {
    backgroundColor: '',
    backgroundImage: '',
    icon: '',
    name: '',
    primaryColor: '',
    secondaryColor: '',
  },
  updatedBy: '',
  updatedDateTime: '',
});

// Helper functions for checking page style compatibility (used in reducers)
const canChangeToGridStyle = (regularSections: PageSection[]): boolean => {
  const hasMultipleRegularSections = regularSections.length > 1;
  const firstSection = regularSections[0];
  const styleTypes = getValidSectionStyleTypes('grid');
  const firstSectionHasInvalidStyleType =
    regularSections.length > 0 && !styleTypes.includes(firstSection?.style.type);

  return !hasMultipleRegularSections && !firstSectionHasInvalidStyleType;
};

const canChangeToSwimlaneStyle = (regularSections: PageSection[]): boolean => {
  const styleTypes = getValidSectionStyleTypes('swimlane');
  const invalidSections = regularSections.filter(
    section => !styleTypes.includes(section.style.type)
  );
  return invalidSections.length === 0;
};

const canChangePageStyle = (regularSections: PageSection[], pageStyle: PageStyle): boolean =>
  pageStyle === 'grid'
    ? canChangeToGridStyle(regularSections)
    : canChangeToSwimlaneStyle(regularSections);

type PageDraftablesState = { [pageId: string]: DetailedPage };

const initialState: PageDraftablesState = {};

export const isRegularSection = (section: PageSection): boolean =>
  RegularSectionStyleTypes.includes(section.style.type as RegularSectionStyleType);

export const isStaticSection = (section: PageSection): boolean =>
  StaticSectionStyleTypes.includes(section.style.type as StaticSectionStyleType);

// Base selector
export const getPage = (pageId: string) => (state: RootState) => state.pageDraftables[pageId];

export const isPageInitialized = (pageId: string) => (state: RootState) =>
  !!state.pageDraftables[pageId];

// Property selectors - extraction logic in input selectors
export const getPageName = (pageId: string) => (state: RootState) =>
  state.pageDraftables[pageId]?.name;

export const getPageSlug = (pageId: string) => (state: RootState) =>
  state.pageDraftables[pageId]?.slug;

export const getPageMenu = (pageId: string) => (state: RootState) =>
  state.pageDraftables[pageId]?.pageMenuSourceId;

export const getPageTheme = (pageId: string) => (state: RootState) =>
  state.pageDraftables[pageId]?.theme;

export const getPageStyle = (pageId: string) => (state: RootState) =>
  state.pageDraftables[pageId]?.style;

export const getPageOrderProvidersBy = (pageId: string) => (state: RootState) =>
  state.pageDraftables[pageId]?.orderProvidersBy;

// Sections
export const getSection = (pageId: string, sectionIndex: number) => (state: RootState) =>
  state.pageDraftables[pageId]?.sections[sectionIndex];

export const getMinifiedPageSection = (pageId: string, sectionIndex: number) =>
  createSelector([getSection(pageId, sectionIndex)], (section): MinifiedPageSection | undefined => {
    if (!section) return undefined;
    const { sources: _, ...ret } = section;
    return ret;
  });

export const getSources = (pageId: string, sectionIndex: number) => (state: RootState) =>
  state.pageDraftables[pageId]?.sections[sectionIndex]?.sources ?? [];

export const getHasSources = (pageId: string, sectionIndex: number) => (state: RootState) =>
  (state.pageDraftables[pageId]?.sections[sectionIndex]?.sources?.length ?? 0) > 0;

export const getSource =
  (pageId: string, sectionIndex: number, index: number) => (state: RootState) =>
    state.pageDraftables[pageId]?.sections[sectionIndex]?.sources?.[index];

// Regular sections - memoized for performance
export const getRegularPageSections = (pageId: string) =>
  createSelector([getPage(pageId)], page => page?.sections.filter(isRegularSection) ?? []);

export const getRegularPageSectionCount = (pageId: string) => (state: RootState) =>
  state.pageDraftables[pageId]?.sections.filter(isRegularSection).length ?? 0;

export const getCanAddRegularPageSection = (pageId: string) =>
  createSelector(
    [getPageStyle(pageId), getPage(pageId)],
    (style, page) => !(style === 'grid' && (page?.sections.length ?? 0) > 0)
  );

export const getRegularPageSectionSlugs = (pageId: string) =>
  createSelector([getRegularPageSections(pageId)], sections =>
    sections.map(section => section.slug)
  );

export const getHasMultipleRegularSections = (pageId: string) =>
  createSelector([getRegularPageSections(pageId)], sections => sections.length > 1);

export const getAllowsGridPage = (pageId: string) =>
  createSelector([getRegularPageSections(pageId)], regularSections => {
    const hasMultipleRegularSections = regularSections.length > 1;
    const firstSection = regularSections[0];
    const styleTypes = getValidSectionStyleTypes('grid');
    const firstSectionHasInvalidStyleType =
      regularSections.length > 0 && !styleTypes.includes(firstSection?.style.type);

    return !hasMultipleRegularSections && !firstSectionHasInvalidStyleType;
  });

export const getInvalidSwimlaneSections = (pageId: string) =>
  createSelector([getRegularPageSections(pageId)], regularSections => {
    const styleTypes = getValidSectionStyleTypes('swimlane');
    return regularSections.filter(section => !styleTypes.includes(section.style.type));
  });

export const getAllowsSwimlanePage = (pageId: string) =>
  createSelector(
    [getInvalidSwimlaneSections(pageId)],
    invalidSections => invalidSections.length === 0
  );

export const getAllowsPageStyleChange = (pageId: string, pageStyle: PageStyle) =>
  createSelector(
    [getAllowsGridPage(pageId), getAllowsSwimlanePage(pageId)],
    (allowsGrid, allowsSwimlane) => (pageStyle === 'grid' ? allowsGrid : allowsSwimlane)
  );

// Special sections
export const getStaticPageSections = (pageId: string) =>
  createSelector([getPage(pageId)], page => page?.sections.filter(isStaticSection) ?? []);

// SLICE
export const pageSlice = createSlice({
  name: 'page',
  initialState,
  reducers: {
    initializeEmptyPage: state => {
      state[NEW_PAGE_ID] = getEmptyPage();
    },

    // PAGE
    setName: (
      state,
      { payload: { languages, value, pageId, slug } }: PayloadAction<SetNamePayload>
    ) => {
      if (value != null) languages?.forEach(lang => (state[pageId].name[lang] = value));
      if (slug) state[pageId].slug = slug;
    },
    setPage: (state, { payload: { pageId, page } }: PayloadAction<SetPagePayload>) => {
      state[pageId] = page;
    },
    setSlug: (state, { payload: { slug, pageId } }: PayloadAction<SetSlugPayload>) => {
      state[pageId].slug = slug;
    },
    setPageMenu: (state, { payload: { menuId, pageId } }: PayloadAction<SetPageMenuPayload>) => {
      if (menuId) state[pageId].pageMenuSourceId = menuId;
      else delete state[pageId].pageMenuSourceId;
    },
    setOrderProvidersById: (
      state,
      { payload: { pageId, id } }: PayloadAction<SetOrderProvidersByIdPayload>
    ) => {
      if (id) state[pageId].orderProvidersBy = { ...state[pageId].orderProvidersBy, id };
      else delete state[pageId].orderProvidersBy?.id;

      if (!Object.values(state[pageId].orderProvidersBy || {}).some(Boolean))
        delete state[pageId].orderProvidersBy;
    },
    setOrderProvidersByType: (
      state,
      { payload: { type, pageId } }: PayloadAction<SetOrderProvidersByTypePayload>
    ) => {
      if (type) state[pageId].orderProvidersBy = { ...state[pageId].orderProvidersBy, type };
      else delete state[pageId].orderProvidersBy?.type;

      if (!Object.values(state[pageId].orderProvidersBy || {}).some(Boolean))
        delete state[pageId].orderProvidersBy;
    },
    setStyle: (state, { payload: { pageStyle, pageId } }: PayloadAction<SetStylePayload>) => {
      state[pageId].style = pageStyle;

      const regularSections = state[pageId].sections.filter(isRegularSection);
      const staticSections = state[pageId].sections.filter(isStaticSection);

      if (!canChangePageStyle(regularSections, pageStyle)) {
        const styleTypes = getValidSectionStyleTypes(pageStyle);
        const defaultStyleType = getDefaultSectionStyleType(pageStyle);
        const defaultStylePattern = getDefaultStylePattern(defaultStyleType);

        if (pageStyle === 'grid') {
          const firstSection = regularSections[0];

          if (firstSection) {
            const stylePatternIsValid = isSectionStylePatternValid(
              defaultStyleType,
              firstSection.style.pattern
            );

            state[pageId].sections = [
              {
                ...firstSection,
                style: {
                  ...firstSection.style,
                  type: defaultStyleType,
                  ...(stylePatternIsValid
                    ? {}
                    : { pattern: defaultStylePattern, repeatLastSegmentInPattern: false }),
                },
              },
              ...staticSections,
            ];
          }
        } else if (pageStyle === 'swimlane') {
          state[pageId].sections = [
            ...regularSections.map(section => {
              if (styleTypes.includes(section.style.type)) return section;

              const stylePatternIsValid = isSectionStylePatternValid(
                defaultStyleType,
                section.style.pattern
              );

              return {
                ...section,
                style: {
                  ...section.style,
                  type: defaultStyleType,
                  ...(stylePatternIsValid
                    ? {}
                    : { pattern: defaultStylePattern, repeatLastSegmentInPattern: false }),
                },
              };
            }),
            ...staticSections,
          ];
        }
      }
    },

    // THEME
    updateTheme: (
      state,
      { payload: { field, value, pageId } }: PayloadAction<UpdateThemePayload>
    ) => {
      if (value) state[pageId].theme[field] = value;
      else delete state[pageId].theme[field];
    },

    // SECTIONS
    addSection: (
      state,
      { payload: { index, section, pageId } }: PayloadAction<AddSectionPayload>
    ) => {
      state[pageId].sections.splice(index, 0, { ...section, sources: [] });
    },
    clearSection: (state, { payload: { index, pageId } }: PayloadAction<ClearSectionPayload>) => {
      state[pageId].sections[index].sources = [];
    },
    duplicateSection: (
      state,
      { payload: { index, pageId } }: PayloadAction<DuplicateSectionPayload>
    ) => {
      const section = state[pageId].sections.at(index);

      if (section) {
        const copiedSection = removeImagesFromPageSection(section);
        state[pageId].sections.splice(
          index + 1,
          0,
          getItemWithRealSlug(
            {
              ...copiedSection,
              sources: copiedSection.sources.map(withNewSourceInstanceId),
            },
            state[pageId].sections
          )
        );
      }
    },
    updateSection: (
      state,
      { payload: { index, section, pageId } }: PayloadAction<UpdateSectionPayload>
    ) => {
      state[pageId].sections[index] = {
        ...section,
        sources: state[pageId].sections[index].sources,
      };
    },
    removeSection: (state, { payload: { index, pageId } }: PayloadAction<RemoveSectionPayload>) => {
      state[pageId].sections.splice(index, 1);
    },
    reorderSections: (
      state,
      { payload: { order, pageId } }: PayloadAction<ReorderSectionsPayload>
    ) => {
      const regularSections = state[pageId].sections.filter(isRegularSection);
      const staticSections = state[pageId].sections.filter(isStaticSection);

      state[pageId].sections = [
        ...regularSections.sort((a, b) => order.indexOf(a.slug) - order.indexOf(b.slug)),
        ...staticSections,
      ];
    },

    // SECTION SOURCES
    addSectionSources: (
      state,
      { payload: { index, sectionIndex, sources, pageId } }: PayloadAction<AddSectionSourcesPayload>
    ) => {
      state[pageId].sections[sectionIndex]?.sources.splice(index, 0, ...sources);
    },
    updateSectionSource: (
      state,
      {
        payload: { index, sectionIndex, source, pageId },
      }: PayloadAction<UpdateSectionSourcePayload>
    ) => {
      state[pageId].sections[sectionIndex].sources[index] = source;
    },
    removeSectionSource: (
      state,
      { payload: { sectionIndex, index, pageId } }: PayloadAction<RemoveSectionSourcePayload>
    ) => {
      state[pageId].sections[sectionIndex].sources.splice(index, 1);
    },
    reorderSectionSources: (
      state,
      { payload: { sectionIndex, sources, pageId } }: PayloadAction<ReorderSectionSourcesPayload>
    ) => {
      state[pageId].sections[sectionIndex].sources = sources;
    },
  },
});

export const { actions: pageActions } = pageSlice;

export default pageSlice.reducer;
